// Core
import React, { useMemo } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Types
import { EdgeDeviceOperationalStatusStatus } from '../../../../../../types/global';

// Components
import {
  LineChart,
  LineChartType,
  Series,
} from '../../../../../../layout/elements/LineChart';
import { Button, ButtonSize, ButtonType } from '../../../../../../layout/elements/Button';
import { EmptyBox } from '../../../../../../components/EmptyBox';
import {
  Check,
  Close,
  LineChart as LineChartIcon,
  Plus,
} from '../../../../../../layout/icons';
import {
  convertEdgeStatus,
  extractValue,
} from '../../../../../../plugins/trendings';

// View
import { CustomChartProps } from './CustomChart.types';
import {
  AggregateValue,
  ChartMode,
  TrendingRawItem,
} from '../Trendings.types';
import {
  SENSOR_CONFIG,
  SERIES_COLOR_PALETTE,
  SensorConfig,
  SensorTag,
} from '../customChartConfig';
import { SeriesChip } from '../../../../../../layout/elements/SeriesChip';
import {
  ChipsRow,
  CustomChartContainer,
  EmptyBoxContainer,
  SelectTagsButtonContainer,
  SeriesChipsContainer,
} from './CustomChart.style';

/* **************************************** COMPONENT ****************************************** */

function CustomChart({
  chartMode,
  hasCondensedData,
  onOpenTagSelection,
  onRemoveTag,
  rawData,
  selectedStatistic,
  selectedTags,
  testId,
}: CustomChartProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const { isXs, isSm, isMd } = useBreakpoint();

  const isMobile: boolean = isXs || isSm || isMd;

  /* **********************************************************************************************
  ******************************************** METHODS ********************************************
  ********************************************************************************************** */

  // chartId includes selectedTags so amCharts5 gets a fresh root/DOM element
  // when the axis configuration changes (new sensor = potentially new Y-axis).
  const chartId: string = useMemo(
    (): string => `vsi-custom-chart-${chartMode}-${selectedTags.join('-')}`,
    [chartMode, selectedTags],
  );

  // Units are distributed alternately across left and right Y-axes (up to MAX_Y_AXES = 4):
  //   index 0 → left,  index 1 → right,  index 2 → left,  index 3 → right
  // This gives at most 2 axes per side, which the LineChart dx-offset logic handles cleanly.
  const series: Series[] = useMemo((): Series[] => {
    const uniqueUnits: string[] = [];
    selectedTags.forEach((tag: SensorTag): void => {
      const { unit } = SENSOR_CONFIG[tag];
      if (!uniqueUnits.includes(unit)) {
        uniqueUnits.push(unit);
      }
    });

    // The fill series goes to the right side when there are units on both sides
    const hasRightAxis: boolean = uniqueUnits.length > 1;

    const edgeStatusSeries: Series = {
      name: t('common.cloudConnection'),
      field: 'edgeStatus',
      type: LineChartType.fill,
      date: 'timestamp',
      opposite: hasRightAxis,
    };

    const tagSeries: Series[] = selectedTags.map((tag: SensorTag, tagIndex: number): Series => {
      const config: SensorConfig = SENSOR_CONFIG[tag];
      const unitIndex: number = uniqueUnits.indexOf(config.unit);
      // Even-indexed units (0, 2) go left; odd-indexed units (1, 3) go right
      const isOpposite: boolean = unitIndex % 2 === 1;
      return {
        bullet: true,
        color: SERIES_COLOR_PALETTE[tagIndex],
        date: 'timestamp',
        decimals: config.decimals,
        field: tag,
        maxScale: config.maxScale,
        measurementType: config.measurementType,
        minScale: config.minScale,
        name: t(config.nameKey),
        opposite: isOpposite,
        type: LineChartType.line,
        unit: config.unit,
      };
    });

    return [edgeStatusSeries, ...tagSeries];
  }, [selectedTags, t]);

  const processedData: Record<string, number | null>[] = useMemo(
    (): Record<string, number | null>[] => {
      const hasData: boolean = !!rawData && rawData.length > 0;

      const data: Record<string, number | null>[] = hasData
        ? rawData.map((raw: TrendingRawItem) => {
          const rawRecord: Record<string, number | AggregateValue | null | undefined> = (
            raw as Record<string, number | AggregateValue | null | undefined>
          );
          const result: Record<string, number | null> = {
            timestamp: rawRecord.timestamp as number,
            edgeStatus: convertEdgeStatus(
              rawRecord.edgeStatus as EdgeDeviceOperationalStatusStatus | number | null,
            ),
          };

          selectedTags.forEach((tag: SensorTag): void => {
            result[tag] = extractValue(
              rawRecord[tag] as number | AggregateValue | null | undefined,
              hasCondensedData,
              selectedStatistic,
            );
          });

          return result;
        })
        : [];

      return data;
    },
    [
      rawData,
      selectedTags,
      hasCondensedData,
      selectedStatistic,
    ],
  );

  /* **********************************************************************************************
  ************************************** COMPONENT HANDLING ***************************************
  ********************************************************************************************** */

  const hasSelectedTags: boolean = selectedTags.length > 0;

  const chartElement: JSX.Element = hasSelectedTags ? (
    <CustomChartContainer data-testid={testId}>
      {/* On mobile the chips row is omitted entirely — the "Configure sensors"
        * button lives in the Trendings header row next to the DatePicker */}
      {!isMobile && (
        <ChipsRow>
          <SeriesChipsContainer data-testid="custom-chart-chips">
            {selectedTags.map((tag: SensorTag, tagIndex: number): JSX.Element => {
              const config: SensorConfig = SENSOR_CONFIG[tag];
              return (
                <SeriesChip
                  key={tag}
                  color={SERIES_COLOR_PALETTE[tagIndex]}
                  dotIcon={<Check />}
                  label={t(config.nameKey)}
                  onRemove={(): void => onRemoveTag(tag)}
                  removeAriaLabel={String(t('common.remove'))}
                  removeIcon={<Close />}
                  removeTestId={`chip-remove-${tag}`}
                  testId={`chip-${tag}`}
                />
              );
            })}
          </SeriesChipsContainer>

          <Button
            block={false}
            onClick={onOpenTagSelection}
            size={ButtonSize.xs}
            testId="select-tags-button"
            type={ButtonType.link}
          >
            <Plus />
            {t('view.crusher.trendings.custom.addSensor')}
          </Button>
        </ChipsRow>
      )}
      <LineChart
        activeBullet={chartMode === ChartMode.live || chartMode === ChartMode.liveAggregate}
        compactLegend
        data={processedData}
        id={chartId}
        series={series}
        testId={`${testId}-line-chart`}
      />
    </CustomChartContainer>
  ) : (
    <CustomChartContainer data-testid={testId}>
      <EmptyBoxContainer>
        <EmptyBox
          description={t('view.crusher.trendings.custom.emptyState.description')}
          icon={<LineChartIcon />}
          title={t('view.crusher.trendings.custom.emptyState.title')}
          testId="custom-chart-empty"
        />
      </EmptyBoxContainer>
      <SelectTagsButtonContainer>
        <Button
          block={false}
          onClick={onOpenTagSelection}
          size={ButtonSize.sm}
          type={ButtonType.primary}
          testId="select-tags-button-empty"
        >
          {t('view.crusher.trendings.custom.selectTags')}
        </Button>
      </SelectTagsButtonContainer>
    </CustomChartContainer>
  );

  return chartElement;
}

export { CustomChart };
